<script setup lang="ts">
import { storeToRefs } from "pinia";
import { ref, type Ref } from "vue";
import type { Option } from "~/core/models/filters";
import { useFetchDtwinConnectivityStatus } from "~/modules/dtwin/composables/useFetchDtwinConnectivityStatus";
import type { DtwinConnectivityStatus } from "~/modules/dtwin/models/DtwinConnectivityStatus.enum";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";

const { t } = useI18n();
const { currentOrganization } = storeToRefs(useOrganizationStore());
const { results, fetchAll, isLoading } = useFetchDtwinConnectivityStatus({
  organizationCode: currentOrganization?.value?.code,
});
const model: Ref<DtwinConnectivityStatus[]> = defineModel<
  DtwinConnectivityStatus[]
>({ required: true });
const connectivityType: Ref<Option[]> = ref([]);

const connectivityTypeOptions = computed(() => {
  let options: Option[] = [];
  if (results.value) {
    options = Object.keys(results.value).map((key) => {
      return {
        label: t(`dtwins.filters.connectivity.${key}`),
        value: key,
      };
    });
  }
  return options;
});

const isAtLeastOneConnectivityTypeSelected = computed(() => {
  return connectivityType.value.length !== 0;
});

const connectivtyStatuses = computed(() => {
  if (results.value && connectivityType.value?.length > 0) {
    const selectedConnectivityType = connectivityType.value[0].value;
    const statuses = results.value[selectedConnectivityType];
    return statuses.map((status: DtwinConnectivityStatus) => {
      return {
        label: t(`dtwins.filters.connectivity.${status}`),
        value: status,
      };
    });
  }
});

onMounted(fetchAll);
</script>
<template>
  <div class="bg-white p-6">
    <h6 class="text-base mb-4 underline text-gray-900 font-medium">
      {{ t("dtwins.filters.connectivity.type") }}
    </h6>
    <filter-selectable-list
      v-if="results"
      v-model="connectivityType"
      :data="connectivityTypeOptions"
      :is-loading="isLoading"
      :empty-text="t('dtwins.filters.connectivity.empty')"
      prop="label"
      item-prop="value"
      use-radio-buttons
    />
    <template v-if="isAtLeastOneConnectivityTypeSelected">
      <h6 class="text-base my-4 underline text-gray-900 font-medium">
        {{ t("dtwins.filters.connectivity.status") }}
      </h6>
      <filter-selectable-list
        v-if="results"
        v-model="model"
        :data="connectivtyStatuses"
        :is-loading="isLoading"
        :empty-text="t('dtwins.filters.connectivity.empty')"
        prop="label"
        item-prop="value"
      />
    </template>
  </div>
</template>
